/**
 * Self-hosted voice agent — Whisper STT + Sesame CSM TTS on RunPod.
 *
 * Flow (integrated with LoRA tutors via api.tutorChat):
 *   1. Record mic audio until silence
 *   2. POST /voice/transcribe → Whisper text
 *   3. Caller sends text to LoRA tutor
 *   4. POST /voice/speak → Sesame WAV → play
 *
 * Uses VITE_VOICE_URL for direct RunPod access, else proxies via /api/voice/*.
 */

const API_BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";
const VOICE_DIRECT =
  (import.meta.env.VITE_VOICE_URL as string | undefined)?.replace(/\/$/, "") ?? "";

export interface VoiceAgentStatus {
  configured: boolean;
  ready: boolean;
  engine: string;
}

let _currentAudio: HTMLAudioElement | null = null;

export function stopVoiceAgentAudio() {
  if (_currentAudio) {
    _currentAudio.pause();
    _currentAudio.src = "";
    _currentAudio = null;
  }
}

export async function fetchVoiceAgentStatus(): Promise<VoiceAgentStatus> {
  if (VOICE_DIRECT) {
    try {
      const r = await fetch(`${VOICE_DIRECT}/ready`);
      if (r.ok) {
        const d = await r.json();
        return { configured: true, ready: !!d.ready, engine: "sesame-whisper" };
      }
    } catch {
      /* fall through */
    }
    return { configured: true, ready: false, engine: "sesame-whisper" };
  }
  try {
    const r = await fetch(`${API_BASE}/voice/status`);
    if (r.ok) {
      const d = await r.json();
      return {
        configured: !!d.configured,
        ready: !!d.ready,
        engine: d.engine || "sesame-whisper",
      };
    }
  } catch {
    /* fall through */
  }
  return { configured: false, ready: false, engine: "browser-fallback" };
}

/** Transcribe recorded audio via Whisper on RunPod. */
export async function transcribeAudio(blob: Blob): Promise<string> {
  const form = new FormData();
  form.append("file", blob, "speech.webm");
  const url = VOICE_DIRECT
    ? `${VOICE_DIRECT}/transcribe`
    : `${API_BASE}/voice/transcribe`;
  const r = await fetch(url, { method: "POST", body: form });
  if (!r.ok) throw new Error(`Transcribe failed: ${r.status}`);
  const data = (await r.json()) as { text?: string };
  return (data.text || "").trim();
}

/** Speak text via Sesame CSM — returns when audio finishes. */
export async function speakSesame(
  text: string,
  opts?: { rewrite?: boolean; onStart?: () => void; onEnd?: () => void },
): Promise<void> {
  const clean = text.trim();
  if (!clean) {
    opts?.onEnd?.();
    return;
  }
  stopVoiceAgentAudio();

  const url = VOICE_DIRECT
    ? `${VOICE_DIRECT}/speak`
    : `${API_BASE}/voice/speak`;
  const body = VOICE_DIRECT
    ? { text: clean, speaker_id: 0 }
    : { text: clean, speaker_id: 0, rewrite: opts?.rewrite ?? true };

  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`Speak failed: ${r.status}`);

  const buf = await r.arrayBuffer();
  const blob = new Blob([buf], { type: "audio/wav" });
  const objectUrl = URL.createObjectURL(blob);
  const audio = new Audio(objectUrl);
  _currentAudio = audio;

  await new Promise<void>((resolve, reject) => {
    audio.onplay = () => opts?.onStart?.();
    audio.onended = () => {
      opts?.onEnd?.();
      URL.revokeObjectURL(objectUrl);
      _currentAudio = null;
      resolve();
    };
    audio.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Audio playback failed"));
    };
    audio.play().catch(reject);
  });
}

/**
 * Records from the microphone until the user pauses (~silenceMs).
 * Calls `onPartial` with live audio level for UI feedback.
 */
export class MicRecorder {
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private raf = 0;
  private analyser: AnalyserNode | null = null;
  private audioCtx: AudioContext | null = null;
  private silenceStart = 0;
  private speaking = false;

  async start(opts: {
    onLevel?: (level: number) => void;
    silenceMs?: number;
    onUtterance: (blob: Blob) => void;
  }): Promise<void> {
    this.stop();
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });

    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";

    this.recorder = new MediaRecorder(this.stream, { mimeType: mime });
    this.chunks = [];

    this.recorder.ondataavailable = (e) => {
      if (e.data.size > 0) this.chunks.push(e.data);
    };

    this.recorder.onstop = () => {
      if (this.chunks.length === 0) return;
      const blob = new Blob(this.chunks, { type: mime });
      this.chunks = [];
      if (blob.size > 800) opts.onUtterance(blob);
    };

    this.recorder.start(250);

    // Silence detection via audio level
    this.audioCtx = new AudioContext();
    const src = this.audioCtx.createMediaStreamSource(this.stream);
    this.analyser = this.audioCtx.createAnalyser();
    this.analyser.fftSize = 512;
    src.connect(this.analyser);

    const silenceMs = opts.silenceMs ?? 1500;
    const data = new Uint8Array(this.analyser.frequencyBinCount);
    const threshold = 0.035;

    const tick = () => {
      if (!this.analyser || !this.recorder) return;
      this.analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      opts.onLevel?.(Math.min(1, rms * 8));

      if (rms > threshold) {
        this.speaking = true;
        this.silenceStart = 0;
      } else if (this.speaking) {
        if (!this.silenceStart) this.silenceStart = Date.now();
        else if (Date.now() - this.silenceStart > silenceMs) {
          this.speaking = false;
          this.silenceStart = 0;
          if (this.recorder.state === "recording") this.recorder.stop();
          // Restart for next utterance
          setTimeout(() => {
            if (this.recorder && this.stream) {
              this.chunks = [];
              try {
                this.recorder.start(250);
              } catch {
                /* may need new recorder */
                this.recorder = new MediaRecorder(this.stream, { mimeType: mime });
                this.recorder.ondataavailable = (e) => {
                  if (e.data.size > 0) this.chunks.push(e.data);
                };
                this.recorder.onstop = () => {
                  if (this.chunks.length === 0) return;
                  const b = new Blob(this.chunks, { type: mime });
                  this.chunks = [];
                  if (b.size > 800) opts.onUtterance(b);
                };
                this.recorder.start(250);
              }
            }
          }, 300);
        }
      }
      this.raf = requestAnimationFrame(tick);
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop() {
    cancelAnimationFrame(this.raf);
    if (this.recorder?.state === "recording") {
      try { this.recorder.stop(); } catch { /* ignore */ }
    }
    this.recorder = null;
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
    this.analyser = null;
    this.audioCtx?.close().catch(() => {});
    this.audioCtx = null;
  }
}
